import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  OpenInNew as ExternalLinkIcon
} from '@mui/icons-material';

/**
 * Search Seller Central returns for an order id or a tracking number.
 *
 * The URL is fetched from the server rather than built here. This component used to
 * hardcode the retired layout
 * (`/gp/returns/list/v2/?searchBy=CarrierTrackingId&searchString=…`), which meant typing an
 * order id searched a *tracking-number* field on the *old* page — Amazon opened the returns
 * page and matched nothing. The format now lives in one tested place,
 * `server/services/amazonSearchUrl.js`, and is served by
 * `GET /api/returns/amazon/search-url?q=…`.
 */
const AmazonTrackingSearch = ({ 
  onSearch, 
  placeholder = "Enter an Amazon order ID or tracking number (e.g., 202-9444544-0740348)", 
  title = "Amazon Returns Search",
  compact = false 
}) => {
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const openAmazonSearch = async () => {
    const value = reference.trim();
    if (!value) return;

    setBusy(true);
    setError(null);

    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(
        `/api/returns/amazon/search-url?q=${encodeURIComponent(value)}`,
        { headers: { ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }) } }
      );

      if (!response.ok) {
        throw new Error('Could not build the Amazon search link');
      }

      const url = (await response.text()).trim();
      if (!url.startsWith('https://')) {
        throw new Error('The server did not return a valid Amazon URL');
      }

      window.open(url, '_blank', 'noopener,noreferrer');
      if (onSearch) onSearch(value, 'amazon-direct');
    } catch (searchError) {
      console.error('Amazon search failed:', searchError);
      setError(searchError.message || 'Could not open Amazon');
    } finally {
      setBusy(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      openAmazonSearch();
    }
  };

  const field = (
    <TextField
      placeholder={placeholder}
      value={reference}
      onChange={(e) => setReference(e.target.value)}
      onKeyPress={handleKeyPress}
      disabled={busy}
      sx={{ flex: 1, minWidth: '300px' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <ExternalLinkIcon sx={{ color: '#FF9900' }} />
          </InputAdornment>
        ),
      }}
    />
  );

  const button = (
    <Button
      variant="contained"
      onClick={openAmazonSearch}
      disabled={busy || !reference.trim()}
      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ExternalLinkIcon />}
      sx={{
        bgcolor: '#FF9900',
        '&:hover': { bgcolor: '#e68a00' },
        whiteSpace: 'nowrap'
      }}
    >
      {busy ? 'Opening…' : 'Search Amazon'}
    </Button>
  );

  if (compact) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {field}
          {button}
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {field}
          {button}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );
};

export default AmazonTrackingSearch;
